import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { Button, Col, DatePicker, Form, Input, Row, Select, Spin, Table, Tooltip } from "antd";
import { uniqueId } from "lodash";
import {
  ServiceTypesDefaultTableRowState,
  ServiceTypesDictionary,
  ServiceTypesPeriod,
  ServiceTypesProps,
  ServiceTypesRecordProps,
  ServiceTypesTableProps,
  ServiceTypesTaxRate,
  ServiceTypesTaxSummary,
} from "./ServiceTypesInterfaces";
import {
  displayFormat,
  toDisplayFormat,
  toDisplayFormatWithTime,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { expandedTableEyeIcon } from "../../lib/icons";
import {
  createServiceTypeVersion,
  deleteServiceTypeVersion,
  fetchServiceTypesAdmin,
  fetchServiceTypesVersions,
} from "../../store/slices/service-types";
import tickIcon from "../../img/done.svg";
import deleteIcon from "../../img/delete-round.svg";
import pencilWithPlusIcon from "../../img/pencil-plus.svg";
import pencilWithCalendarIcon from "../../img/pencil-calendar.svg";
import {
  fetchChargeNameTypeStatus,
  fetchProducts,
  fetchServiceMode,
} from "../../store/slices/rtkDictionaries";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_SERVICETYPE_WRITE } from "../../lib/const/permissions";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { CharacteristicsContainer } from "../../modules/policies";
import { useCharacteristicsInitialisation } from "../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import { ReactComponent as CharacteristicIconOpen } from "../../img/characteristic_icons/characteristicIconOpen.svg";
import { ReactComponent as CharacteristicIconClose } from "../../img/characteristic_icons/characteristicIconClose.svg";
import { getCharFormatItem } from "../../modules/policies/services/getCharFormatItem";
import { useAppDispatch } from "../..";
import "./ServiceTypes.less";

const ServiceTypes: React.FC<ServiceTypesProps> = ({
  onDate,
  serviceTypes,
  serviceTypesLoading,
  onChangeDate,
  toggleServiceTypesByDate,
}) => {
  const {
    serviceTypesVersions,
    serviceTypesVersionsLoading,
    taxesRates,
    taxesRatesLoading,
  } = useSelector((state: RootStateOrAny) => state.serviceTypes);
  const { serviceMode, products, chargeNameTypeStatus } = useSelector(
    (state: RootStateOrAny) => state.rtkDictionaries
  );

  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [serviceTypeVersionId, setServiceTypeVersionId] = useState<number>(-1);
  const [dataVersion, setDataVersion] = useState<ServiceTypesRecordProps[]>([]);

  const [editingKey, setEditingKey] = useState("");
  const [formCurrencyEditStatus, setFormCurrencyEditStatus] = useState("idle");
  const isEditing = (rec: ServiceTypesRecordProps) => rec.key === editingKey;
  const isEditingToDate = formCurrencyEditStatus === "isEditingToDate";
  const isMakingNewVersion = formCurrencyEditStatus === "createNewVersion";
  const dateFormat = displayFormat;
  const nowDate = toHttpFormat(moment());
  const [visibleModal, setVisibleModal] = useState(false);
  const [objectId, setObjectId] = useState<number>(0);
  const [objectDateFrom, setObjectDateFrom] = useState("");
  const [objectDateTo, setObjectDateTo] = useState("");

  const { Option } = Select;

  const initialValue = 1535;

  const [widthRatio, setWidthRatio] = useState(window.innerWidth / initialValue);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1200) setWidthRatio(window.innerWidth / initialValue);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const columnFlexs: Record<any | "btns", string> = {
    id: "100px",
    name: `${120 * widthRatio}px`,
    serviceCode: `${160 * widthRatio}px`,
    serviceModeId: `${160 * widthRatio}px`,
    taxRates: `${60 * widthRatio}px`,
    productId: `${60 * widthRatio}px`,
    chargeNameId: `${300 * widthRatio}px`,
    period: `${150 * widthRatio}px`,
    x: `1%`,
  };

  const [
    serviceTypeRequestBody,
    setServiceTypeRequestBody,
  ] = useState<ServiceTypesDefaultTableRowState>({
    id: 0,
    fromDate: "",
    toDate: "",
    name: "",
    productId: 0,
    serviceModeId: null,
    chargeNameId: null,
    taxRateMask: 0,
    taxTypeMask: null,
    dsc: "",
    baseServiceTypeId: 0,
    serviceCode: "",
    tenantId: 0,
    // taxes: [],
  });

  let tableData;

  const [filterValues, setFilterValues] = useState<{ name?: "string" }>({});

  const handleSetFilterValues = (prop) => {
    setFilterValues((prev) => ({ ...prev, ...prop }));
  };

  const renderNameColumnTitle = () => (
    <Input
      onChange={(e) => handleSetFilterValues({ name: e.target.value })}
      value={filterValues.name}
      placeholder="Введите наименование"
      style={{ width: "100%" }}
      allowClear
    />
  );

  renderNameColumnTitle.toString = () => "Наименование";

  if (checkIsLoaded(serviceTypesLoading)) {
    tableData = serviceTypes
      ?.map((item: ServiceTypesTableProps, index: number) => ({
        key: index,
        id: item.id,
        name: item?.name,
        fromDate: item.fromDate,
        toDate: item.toDate,
        productId: item.productId,
        serviceModeId: item.serviceModeId,
        chargeNameId: item.chargeNameId,
        taxRateMask: item.taxRateMask,
        taxTypeMask: item.taxTypeMask,
        dsc: item.dsc,
        baseServiceTypeId: item.baseServiceTypeId,
        serviceCode: item.serviceCode,
        tenantId: item.tenantId,
        period: item,
        taxes: item?.taxes,
        taxRates: item?.taxRates,
      }))
      .filter((productVersion) => {
        if (filterValues.name) {
          return String(productVersion?.name)
            .toLowerCase()
            .includes(String(filterValues.name).toLowerCase());
        }
        return productVersion;
      });
  }

  useEffect(() => {
    if (checkIsLoaded(serviceTypesVersionsLoading))
      setDataVersion(
        serviceTypesVersions &&
          serviceTypesVersions?.map((item: ServiceTypesRecordProps, index: number) => ({
            key: uniqueId(),
            id: item.id,
            name: item?.name,
            fromDate: item.fromDate,
            toDate: item.toDate,
            productId: item.productId,
            serviceModeId: item.serviceModeId,
            chargeNameId: item.chargeNameId,
            taxRateMask: item.taxRateMask,
            taxTypeMask: item.taxTypeMask,
            dsc: item.dsc,
            baseServiceTypeId: item.baseServiceTypeId,
            serviceCode: item.serviceCode,
            tenantId: item.tenantId,
            currentDate: moment().isBetween(item.fromDate, item.toDate),
            pastDate: moment(item.fromDate).isBefore(nowDate),
            futureDate: moment(item.fromDate).isAfter(nowDate),
            fromDateForRequestBody: item.fromDate,
            toDateForRequestBody: item.toDate,
            lastVersion: String(index) === String(0) ? "lastVersion" : "",
            taxes: item?.taxes,
            taxRates: item?.taxRates,
            info: item?.info,
          }))
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTypesVersions]);

  const handleShowModal = (objId: ServiceTypesTableProps) => {
    setVisibleModal(true);
    setObjectDateFrom(objId.fromDate);
    setObjectDateTo(objId.toDate);
    setObjectId(objId.id);
  };

  const handleHideModal = () => {
    setVisibleModal(false);
    setObjectId(0);
  };

  const getColumns = () => [
    {
      title: "ID типа",
      dataIndex: "id",
      key: "id",
      width: columnFlexs.id,
      hidden: true,
    },
    {
      title: renderNameColumnTitle,
      dataIndex: "name",
      key: "name",
      width: columnFlexs.name,
      // width: "18%",
    },
    {
      title: "Код",
      dataIndex: "serviceCode",
      key: "serviceCode",
      width: columnFlexs.serviceCode,
      // width: "7%",
    },
    {
      title: "Вид",
      dataIndex: "serviceModeId",
      key: "serviceModeId",
      width: columnFlexs.serviceModeId,
      // width: "10%",
      render: (val: number) => {
        const serviceTypeName =
          serviceMode && serviceMode.find((item: ServiceTypesDictionary) => item?.id === val);

        return <div>{serviceTypeName?.name}</div>;
      },
    },
    {
      title: "Налоги",
      dataIndex: "taxRates",
      key: "taxRates",
      width: columnFlexs.taxRates,
      // width: "9%",
      render: (val: ServiceTypesTaxSummary[]) => {
        // const ratesName = val ? val.map((item: ServiceTypesTaxSummary) => item?.rate?.name) : "-";
        const ratesName = val
          ? val.map((item: ServiceTypesTaxSummary) => `${item?.taxRateName}; `)
          : "-";

        return (
          <div style={{ whiteSpace: "nowrap" }}>
            {ratesName && ratesName.length !== 0 ? (
              ratesName
            ) : (
              <span style={{ paddingLeft: "15px" }}>-</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Группа продуктов",
      dataIndex: "productId",
      key: "productId",
      width: columnFlexs.productId,
      // width: "10%",
      render: (val: number) => {
        const productName =
          products && products.find((item: ServiceTypesDictionary) => item?.id === val);

        return <div>{productName?.name}</div>;
      },
    },
    {
      title: "Номенклатура",
      dataIndex: "chargeNameId",
      key: "chargeNameId",
      width: columnFlexs.chargeNameId,
      // width: "15%",
      render: (val: number) => {
        const serviceTypeName =
          chargeNameTypeStatus &&
          chargeNameTypeStatus.find((item: ServiceTypesDictionary) => item?.id === val);

        return <div>{serviceTypeName?.name}</div>;
      },
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnFlexs.period,
      // width: "12%",
      render: (record: ServiceTypesPeriod) =>
        `${record.fromDate ? toDisplayFormat(record.fromDate) : ""} - ${
          record.toDate ? toDisplayFormat(record.toDate) : ""
        }`,
    },
    {
      title: "",
      dataIndex: "",
      key: "button",
      width: "2%",
      render: (record: ServiceTypesRecordProps) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleShowModal(record);
          }}
          className="button-icon iconButton"
          style={{ outline: "none", cursor: "pointer" }}
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columns, setColumns] = useState<any[]>(getColumns());

  // Обновление колонок (для отображения фильтров в заголовках) с сохранением состояния hidden
  useEffect(() => {
    setColumns((prev) => {
      const newColumns = getColumns();
      const getNewColumn = ({ key }) => newColumns.find((item) => item.key === key);
      return prev.map((prevColumn) => {
        const newColumn = getNewColumn(prevColumn);
        if (newColumn) {
          return {
            ...prevColumn,
            title: newColumn.title,
            render: newColumn.render,
            width: columnFlexs[prevColumn.key] || prevColumn.width,
          };
        }
        return prevColumn;
      });
    });
  }, [serviceMode, chargeNameTypeStatus, products, widthRatio]);

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    onCheckFieldsError,
    fieldsError,
    infoCharacteristic,
    setInfoCharacteristic,
    onExpandAddVersionForCharacteristics,
    onChangeExpandedRowKeysCharacteristic,
    expandedRowKeysCharacteristic,
  } = useCharacteristicsInitialisation({
    initialFromDate: moment().startOf("day"),
  });
  // ---> Характеристики <---

  const getColumnsInner = () => [
    {
      title: "ID типа",
      dataIndex: "id",
      key: "id",
      width: columnFlexs.id,
      hidden: true,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnFlexs.name,
      // width: "18%",
      render: (val: string, record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);

        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            name: e.target.value,
          });
        };

        return editable && isMakingNewVersion ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input onChange={onChange} value={serviceTypeRequestBody.name} defaultValue={val} />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Код",
      dataIndex: "serviceCode",
      key: "serviceCode",
      width: columnFlexs.serviceCode,
      // width: "7%",
      render: (val: string, record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);

        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            serviceCode: e.target.value,
          });
        };

        return editable && isMakingNewVersion ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="serviceCode"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              onChange={onChange}
              value={serviceTypeRequestBody.serviceCode}
              defaultValue={val}
            />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Вид",
      dataIndex: "serviceModeId",
      key: "serviceModeId",
      width: columnFlexs.serviceModeId,
      // width: "10%",
      render: (val: number) => {
        const serviceTypeName =
          serviceMode && serviceMode.find((item: ServiceTypesDictionary) => item?.id === val);

        return <>{serviceTypeName?.name}</>;
      },
    },
    {
      title: "Налоги",
      dataIndex: "taxRates",
      key: "taxRates",
      width: columnFlexs.taxRates,
      // width: "9%",
      render: (val: ServiceTypesTaxSummary[], record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);
        // const ratesName = val ? val.map((item: ServiceTypesTaxSummary) => item?.rate?.name) : "-";
        const ratesName = val
          ? val.map((item: ServiceTypesTaxSummary) => `${item?.taxRateName}; `)
          : "-";
        const ides = val.map((item: ServiceTypesTaxSummary) => item?.taxRateId);

        const onChangeTaxes = (numbers: number[]) => {
          const taxSum = numbers.reduce((sum: number, cur: number) => sum + cur, 0);
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            taxRateMask: taxSum,
          });
        };

        return editable && isMakingNewVersion ? (
          <>
            <Select
              onChange={onChangeTaxes}
              defaultValue={ides}
              style={{ width: "100%" }}
              mode="multiple"
              showArrow
            >
              {checkIsLoaded(taxesRatesLoading) &&
                taxesRates.map((item: ServiceTypesTaxRate) => (
                  <Option key={item.id} value={item.taxRateId}>
                    {item.taxRateName ? item.taxRateName : "-"}
                  </Option>
                ))}
            </Select>
          </>
        ) : (
          <div style={{ whiteSpace: "nowrap" }}>
            {ratesName && ratesName.length !== 0 ? (
              ratesName
            ) : (
              <span style={{ paddingLeft: "15px" }}>-</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Продукт",
      dataIndex: "productId",
      key: "productId",
      width: columnFlexs.productId,
      // width: "10%",
      render: (val: number, record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);

        const productName =
          products && products.find((item: ServiceTypesDictionary) => item?.id === val);

        const onChange = (e: number) => {
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            productId: e,
          });
        };

        return editable && isMakingNewVersion ? (
          <div>
            <Form.Item
              className="form-item-required__hidden-error__mb0"
              initialValue={val}
              name="productId"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select style={{ width: "100%" }} defaultValue={val} onChange={onChange}>
                {products &&
                  products.map((item: ServiceTypesDictionary) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </div>
        ) : (
          <>{productName?.name}</>
        );
      },
    },
    {
      title: "Номенклатура",
      dataIndex: "chargeNameId",
      key: "chargeNameId",
      width: columnFlexs.chargeNameId,
      // width: "15%",
      render: (val: number, record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);

        const serviceTypeName =
          chargeNameTypeStatus &&
          chargeNameTypeStatus.find((item: ServiceTypesDictionary) => item?.id === val);

        const onChange = (e: number) => {
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            chargeNameId: e,
          });
        };

        return editable && isMakingNewVersion ? (
          <div style={{ width: 300 }}>
            <Form.Item
              className="form-item-required__hidden-error__mb0"
              initialValue={val}
              name="chargeNameId"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select style={{ width: "100%" }} defaultValue={val} onChange={onChange}>
                {chargeNameTypeStatus &&
                  chargeNameTypeStatus.map((item: ServiceTypesDictionary) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </div>
        ) : (
          <>{serviceTypeName?.name}</>
        );
      },
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnFlexs.period,
      // width: "12%",
      render: (val: ServiceTypesPeriod, record: ServiceTypesRecordProps) => {
        const editable = isEditing(record);

        const fromDateValue = moment(
          toDisplayFormat(record.fromDateForRequestBody),
          "DD.MM.YYYY"
        ).add(1, "day");

        const toDateValue = moment(toDisplayFormat(record.toDateForRequestBody), "DD.MM.YYYY");

        const fromDateAndToDateAreEqual = (): boolean =>
          toDisplayFormat(record.fromDateForRequestBody) ===
          toDisplayFormat(record.toDateForRequestBody);

        const onChangeFromDate = (e: moment.Moment | null): void => {
          onChangeFromDateCharacteristic(e);
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            fromDate: toHttpFormatStartOfDay(e),
          });
        };

        const onChangeToDate = (e: moment.Moment | null): void => {
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            toDate: toHttpFormatEndOfDay(e),
          });
        };

        if (editable && isEditingToDate) {
          return (
            <>
              {record.fromDateForRequestBody ? toDisplayFormat(record.fromDateForRequestBody) : ""}{" "}
              -{" "}
              <DatePicker
                format={dateFormat}
                onChange={onChangeToDate}
                defaultValue={toDateValue}
                disabledDate={(current) => current < fromDateValue}
                allowClear={false}
                style={{ width: "110px" }}
              />
            </>
          );
        }

        const disabledDates = (cur: moment.Moment): boolean => {
          if (moment(record.fromDateForRequestBody).isBefore(moment().startOf("month"))) {
            return cur.isBefore(moment().startOf("month"));
          }
          return cur.isBefore(fromDateValue) || cur.isAfter(toDateValue);
        };

        const defaultValues = (): moment.Moment => {
          if (moment(record.fromDateForRequestBody).isBefore(moment().startOf("month"))) {
            return moment().startOf("month");
          }
          return fromDateValue;
        };

        return editable && isMakingNewVersion ? (
          <>
            {fromDateAndToDateAreEqual() ? (
              <DatePicker
                format={dateFormat}
                onChange={onChangeFromDate}
                disabledDate={disabledDates}
                allowClear={false}
                style={{ width: "110px" }}
              />
            ) : (
              <DatePicker
                format={dateFormat}
                onChange={onChangeFromDate}
                defaultValue={defaultValues()}
                disabledDate={disabledDates}
                allowClear={false}
                style={{ width: "110px" }}
              />
            )}
            - {record.toDateForRequestBody ? toDisplayFormat(record.toDateForRequestBody) : ""}
          </>
        ) : (
          <>
            {" "}
            {record.fromDateForRequestBody
              ? toDisplayFormat(record.fromDateForRequestBody)
              : ""} -{" "}
            {record.toDateForRequestBody ? toDisplayFormat(record.toDateForRequestBody) : ""}
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      render: (val: ServiceTypesRecordProps, record: ServiceTypesRecordProps, index: number) => {
        const editable = isEditing(record);
        const disabled = isMakingNewVersion || isEditingToDate;

        const nameValue = val.name;
        const serviceCodeValue = val.serviceCode;
        const serviceModeIdValue = val.serviceModeId;
        const chargeNameIdValue = val.chargeNameId;
        const taxRateMaskValue = val.taxRateMask;
        const productIdValue = val.productId;
        const fromDateValue = val.fromDateForRequestBody;

        const handleEdit = (currencyEditStatus: string) => () => {
          form.resetFields();
          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            name: nameValue,
            serviceCode: serviceCodeValue,
            serviceModeId: serviceModeIdValue,
            chargeNameId: chargeNameIdValue,
            taxRateMask: taxRateMaskValue,
            productId: productIdValue,
            fromDate: fromDateValue,
            toDate: toHttpFormatEndOfDay(record.toDateForRequestBody),
          });
          setEditingKey(() => record.key);
          setFormCurrencyEditStatus(currencyEditStatus);
        };

        const addNewVersion = (currencyEditStatus: string) => () => {
          form.resetFields();
          const fromDateParse = () => {
            if (moment(record.fromDateForRequestBody).isBefore(moment().startOf("month"))) {
              return toHttpFormatStartOfDay(moment().startOf("month"));
            }
            return toHttpFormatStartOfDay(
              moment(
                toDisplayFormatWithTime(record.fromDateForRequestBody),
                "DD.MM.YYYY HH:mm:ss"
              ).add(1, "day")
            );
          };

          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            name: nameValue,
            serviceCode: serviceCodeValue,
            chargeNameId: chargeNameIdValue,
            taxRateMask: taxRateMaskValue,
            productId: productIdValue,
            fromDate:
              toDisplayFormat(record.fromDateForRequestBody) ===
              toDisplayFormat(record.toDateForRequestBody)
                ? ""
                : fromDateParse(),
          });

          const newKey = `new${uniqueId()}`;

          setEditingKey(() => newKey);

          // Для характеристик
          onChangeExpandedRowKeysCharacteristic(newKey);
          onExpandAddVersionForCharacteristics(record.fromDateForRequestBody);

          setFormCurrencyEditStatus(currencyEditStatus);

          const newRecord = {
            ...dataVersion[index],
            key: newKey,
          };

          setDataVersion((prev) => [...prev.slice(0, index), newRecord, ...prev.slice(index)]);
        };

        const handleCancel = () => {
          setEditingKey("");
          form.resetFields();
          if (isMakingNewVersion) {
            onChangeExpandedRowKeysCharacteristic(record.key);
            setDataVersion((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
          }

          setFormCurrencyEditStatus("idle");

          setServiceTypeRequestBody({
            ...serviceTypeRequestBody,
            id: 0,
            fromDate: "",
            toDate: "",
            name: "",
            productId: null,
            // serviceModeId: null,
            chargeNameId: null,
            taxRateMask: 0,
            taxTypeMask: null,
            dsc: "",
            baseServiceTypeId: 0,
            serviceCode: "",
            tenantId: 0,
          });
        };

        const handleSubmit = () => {
          const afterUpdate = () => {
            dispatch(fetchServiceMode());
            dispatch(fetchProducts());
            dispatch(fetchChargeNameTypeStatus());
            dispatch(fetchServiceTypesAdmin());
            dispatch(fetchServiceTypesVersions(serviceTypeVersionId));

            setEditingKey("");
            setFormCurrencyEditStatus("idle");
            form.resetFields();
            setServiceTypeRequestBody({
              ...serviceTypeRequestBody,
              id: 0,
              fromDate: "",
              toDate: "",
              name: "",
              productId: null,
              // serviceModeId: null,
              chargeNameId: null,
              taxRateMask: 0,
              taxTypeMask: null,
              dsc: "",
              baseServiceTypeId: 0,
              serviceCode: "",
              tenantId: 0,
            });
          };
          form.validateFields().then(() => {
            const dataCreate = {
              ...serviceTypeRequestBody,
              id: record.id,
              fromDate: serviceTypeRequestBody.fromDate,
              toDate: record.toDateForRequestBody,
              name: serviceTypeRequestBody.name,
              productId: serviceTypeRequestBody.productId,
              serviceModeId: record.serviceModeId,
              chargeNameId: serviceTypeRequestBody.chargeNameId,
              taxRateMask: serviceTypeRequestBody.taxRateMask,
              taxTypeMask: record.taxTypeMask,
              baseServiceTypeId: record.baseServiceTypeId,
              serviceCode: serviceTypeRequestBody.serviceCode,

              dsc: record.dsc,
              tenantId: record.tenantId,
            };

            if (isEditingToDate)
              dispatch(
                deleteServiceTypeVersion({
                  id: record.id,
                  fromDate: serviceTypeRequestBody.toDate,
                })
              ).then(() => {
                afterUpdate();
              });
            if (isMakingNewVersion) {
              const isValid = onCheckFieldsError(["infoCharacteristic"]);

              if (!isValid) {
                return;
              }

              const info = infoCharacteristic.map((el) => getCharFormatItem(el, 1037));

              dataCreate.info = info;

              dispatch(
                createServiceTypeVersion({
                  id: record.id,
                  fromDate: serviceTypeRequestBody.fromDate,
                  dataValues: dataCreate,
                })
              ).then(() => {
                afterUpdate();
              });
            }
          });
        };

        return editable ? (
          <div style={{ whiteSpace: "nowrap", textAlign: "end" }}>
            <Tooltip title="Подтвердить">
              <Button
                icon={<img src={tickIcon} alt="tick" width="16px" />}
                className="icon"
                type="text"
                htmlType="submit"
                onClick={handleSubmit}
              />
            </Tooltip>
            <Tooltip title="Отменить">
              <Button
                icon={<img src={deleteIcon} width="16px" alt="delete" />}
                type="text"
                className="icon"
                htmlType="button"
                onClick={handleCancel}
              />
            </Tooltip>
          </div>
        ) : (
          <div style={{ whiteSpace: "nowrap", textAlign: "end" }}>
            {hasAccess(MENU_ADMIN_SERVICETYPE_WRITE) && (
              <Tooltip title="Добавить версию">
                {(record.futureDate || record.currentDate) && (
                  <Button
                    icon={<img src={pencilWithPlusIcon} alt="pencil with plus" width="16px" />}
                    type="text"
                    htmlType="button"
                    onClick={addNewVersion("createNewVersion")}
                    disabled={disabled}
                  />
                )}
              </Tooltip>
            )}

            {hasAccess(MENU_ADMIN_SERVICETYPE_WRITE) && (
              <Tooltip title="Изменить дату закрытия">
                {record.lastVersion && (
                  <Button
                    icon={
                      <img src={pencilWithCalendarIcon} alt="pencil with calendar" width="18px" />
                    }
                    type="text"
                    htmlType="button"
                    disabled={disabled}
                    onClick={handleEdit("isEditingToDate")}
                  />
                )}
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      dataIndex: "",
      key: "button",
      width: "2%",
      render: (record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleShowModal(record);
          }}
          className="button-icon iconButton"
          style={{ outline: "none" }}
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columnsInner, setColumnsInner] = useState<any[]>(getColumnsInner());

  // Обновление колонок (для отображения фильтров в заголовках) с сохранением состояния hidden
  useEffect(() => {
    setColumnsInner((prev) => {
      const newColumnsInner = getColumnsInner();
      const getNewColumn = ({ key }) => newColumnsInner.find((item) => item.key === key);
      return prev.map((prevColumn) => {
        const newColumn = getNewColumn(prevColumn);
        if (newColumn) {
          return {
            ...prevColumn,
            title: newColumn.title,
            render: newColumn.render,
            width: columnFlexs[prevColumn.key] || prevColumn.width,
          };
        }
        return prevColumn;
      });
    });
  }, [
    serviceTypeRequestBody,
    dataVersion,
    serviceMode,
    taxesRates,
    products,
    chargeNameTypeStatus,
    infoCharacteristic,
    widthRatio,
  ]);

  const buildRowClassName = (record: ServiceTypesRecordProps): string => {
    const editable = isEditing(record);
    const editNew = record.key.includes("new");
    const activeClassName = "currency-version-row";

    if (record.currentDate && !editable) return `${activeClassName} current-date`;
    if (record.currentDate && editable && !editNew)
      return `${activeClassName} current-date active-row`;
    if (record.currentDate && editable && editNew)
      return `${activeClassName} current-date active-row new-prop` || "";
    if (record.pastDate && !editable) return `${activeClassName} past-date`;
    if (record.pastDate && editable) return `${activeClassName} past-date active-row`;
    if (record.futureDate && !editable) return `${activeClassName} future-date`;
    if (record.futureDate && editable) return `${activeClassName} future-date active-row`;
    return "";
  };

  const innerTable = () => {
    if (!checkIsLoading(serviceTypesVersionsLoading)) {
      return (
        <div className="version-inner-table">
          <Form form={form}>
            <Table
              className="inner-table-service-types"
              size="small"
              columns={columnsInner
                .filter(({ hidden }) => !hidden)
                .map((el, i) => (i === 0 ? { ...el, width: `calc(${el.width} - 40px)` } : el))}
              dataSource={dataVersion}
              pagination={false}
              showHeader={false}
              rowClassName={buildRowClassName}
              loading={checkIsLoading(serviceTypesVersionsLoading)}
              expandable={{
                expandedRowKeys: expandedRowKeysCharacteristic,
                expandedRowRender: (record) => {
                  const editable = isEditing(record);
                  return (
                    <CharacteristicsContainer
                      objectTypeId={1037}
                      mode={editable && isMakingNewVersion ? "add" : "view"}
                      title="Характеристики Типа продукта"
                      tableData={infoCharacteristic}
                      setTableData={setInfoCharacteristic}
                      dataSaved={record?.info}
                      fromDateCharacteristic={fromDateCharacteristic}
                      messageWarningNotFromDate="Необходимо выбрать Дату создания Типа продукта"
                      fieldsError={fieldsError}
                      charIcoNotVisible
                      isChildCharacteristic
                      isDefaultOpen
                    />
                  );
                },
                expandIcon: ({ expanded, record }) => {
                  const onClick = () => {
                    onChangeExpandedRowKeysCharacteristic(record.key);
                  };

                  return (
                    <Tooltip title="Просмотреть характеристики" placement="topLeft">
                      {expanded ? (
                        <CharacteristicIconOpen style={{ cursor: "pointer" }} onClick={onClick} />
                      ) : (
                        <CharacteristicIconClose style={{ cursor: "pointer" }} onClick={onClick} />
                      )}
                    </Tooltip>
                  );
                },
              }}
            />
          </Form>
        </div>
      );
    }
    return <Spin className="spin" />;
  };

  return (
    <>
      <div className="inner-row" style={{ marginBottom: "15px" }}>
        <Row align="middle" justify="space-between">
          <h1>Типы продуктов</h1>
        </Row>
        <Row align="middle" justify="space-between">
          <Col span={10}>
            <label style={{ marginRight: "10px" }}>Дата:</label>
            <DatePicker
              value={onDate}
              onChange={onChangeDate}
              allowClear={false}
              format={dateFormat}
            />
            <Button
              onClick={() => toggleServiceTypesByDate()}
              type="primary"
              style={{ marginLeft: "15px" }}
            >
              Применить
            </Button>
          </Col>
          <Col>
            {hasAccess(MENU_ADMIN_SERVICETYPE_WRITE) && (
              <Link to="service-types/create">
                <Button type="primary">Создать тип продукта</Button>
              </Link>
            )}
          </Col>
        </Row>
      </div>
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns
          tableName="service-types-table"
          columns={columns}
          setColumns={setColumns}
          setChildTableColumns={setColumnsInner}
        />
      </Row>

      <Table
        className="version-table"
        size="small"
        columns={columns
          .filter(({ hidden }) => !hidden)
          .map((el, i) => (i === 0 ? { ...el, className: "first-main-col-for-char" } : el))}
        dataSource={tableData}
        pagination={false}
        loading={checkIsLoading(serviceTypesLoading)}
        expandIconColumnIndex={columns.filter(({ hidden }) => !hidden).length - 1}
        // scroll={{ x: 1200 }}
        //  scroll={{ x: 'max-content' }
        expandedRowKeys={expandedRowKeys}
        expandedRowRender={(a, b, c, expanded) => (expanded ? <>{innerTable()}</> : null)}
        expandIcon={({ expanded, record }) => {
          const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
            setDataVersion([]);
            form.resetFields();
            e.stopPropagation();
            if (!expanded) {
              setExpandedRowKeys([record.key]);
              setServiceTypeVersionId(record.id);
              setFormCurrencyEditStatus("idle");
              setEditingKey("");
              dispatch(fetchServiceTypesVersions(record.id));
            } else {
              setExpandedRowKeys([]);
              // setServiceTypeVersionId(null);
            }
          };
          return (
            <div className="version-table__eye-wrapper" style={{ width: "100%" }}>
              <Button
                icon={expandedTableEyeIcon(expanded ? "#0E5298" : "#86ACD2")}
                onClick={handleClick}
                type="text"
                htmlType="button"
                className="iconButton"
              />
            </div>
          );
        }}
      />

      <HistoryAndPropertyModal
        objectTypeSysname="service_type"
        objectId={objectId}
        handleHideModal={handleHideModal}
        visibleModal={visibleModal}
        dateObjectFromForCalendar={objectDateFrom}
        dateObjectToForCalendar={objectDateTo}
        hasAccessToEdit={hasAccess(MENU_ADMIN_SERVICETYPE_WRITE)}
      />
    </>
  );
};

export default ServiceTypes;
